import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Plus, Trash2, ChevronDown, ChevronUp, AlertTriangle, CheckCircle,
  Plane, Hotel, Car, ParkingSquare, ArrowLeftRight, UtensilsCrossed, MoreHorizontal, ExternalLink, Wallet,
} from 'lucide-react';
import { collection, onSnapshot, query, where, orderBy, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { CITIES } from '../../data/cities';
import TravellerAccessibilityCard from './TravellerAccessibilityCard';
import ProposedHotelCard from '../hotels/ProposedHotelCard';
import ProposedFlightCard from '../flights/ProposedFlightCard';
import {
  tripRequiresApproval, hotelFlowMode, hasPendingOption,
  sectorPatchFromOption, sectorPatchFromBooking, encodeSavedOption,
} from '../../utils/hotelBooking';
import {
  flightFlowMode, hasPendingFlightOption, encodeSavedFlight,
  sectorPatchFromFlightOption, sectorPatchFromFlightBooking,
  flightPassengersFor, flightPassengerParam, newBookingPassengers,
} from '../../utils/flightBooking';
import {
  normName as passengerNameNorm, passengerMatchesName, resolveTravellerProfile,
} from '../../utils/passengerNames';
import { sectorReportingCity } from '../../utils/reportHelpers';
import { buildBookingUrl, BOOKING_URL_KEYS } from '../../utils/bookingContext';
import { needsApproverNomination, eligibleApprovers } from '../../hooks/useApprovalScope';
import { ROLE_LABELS, isApprovalExempt } from '../../utils/permissions';
import { FLIGHT_SSR_OPTIONS } from '../../data/ssrCodes';
import { useTravelCredits } from '../../hooks/useTravelCredits';
import {
  availableCreditsFor, creditBalance, validateCreditApplications, allocateOldestFirst,
} from '../../utils/travelCredits';

const SECTOR_TYPES = {
  flight:        { label: 'Flight',        Icon: Plane },
  accommodation: { label: 'Accommodation', Icon: Hotel },
  'car-hire':    { label: 'Car Hire',       Icon: Car },
  parking:       { label: 'Parking',        Icon: ParkingSquare },
  transfers:     { label: 'Transfers',      Icon: ArrowLeftRight },
  meals:         { label: 'Meals',          Icon: UtensilsCrossed },
  other:         { label: 'Other',          Icon: MoreHorizontal },
};

const DEFAULT_TRIP_TYPES = ['Self-Managed', 'STX-Managed', 'Group Event'];

// ── Traveller name matching ───────────────────────────────────────────────────
// Collapses runs of whitespace so a multi-word surname ("Neville Butcher") or a stray double
// space still compares equal to the same name typed normally.
const normName = passengerNameNorm;

// Display name for a user record, falling back to the email when a profile has no name on it yet.
const memberName = m => [m?.firstName, m?.lastName].filter(Boolean).join(' ').trim() || m?.email || '';

// Name matching lives in utils/passengerNames.js — the flight flow needs the same rule, and a
// second copy that drifts is worse than no copy at all.
const CABIN_CLASSES  = ['Economy', 'Premium Economy', 'Business', 'First'];
const TRANSFER_TYPES = ['Taxi', 'Ride Share', 'Private Car', 'Shuttle', 'Accessible Vehicle', 'Other'];
const MEAL_TYPES     = ['Breakfast', 'Morning Tea', 'Lunch', 'Afternoon Tea', 'Dinner', 'Event Catering'];


const ACCOM_ACCESSIBILITY_OPTIONS = [
  { key: 'roll_in_shower',     label: 'Roll-in shower' },
  { key: 'grab_rails',         label: 'Grab rails / bathroom support' },
  { key: 'bath_hoist',         label: 'Bath hoist / shower chair' },
  { key: 'ground_floor',       label: 'Ground floor or lift access required' },
  { key: 'wide_doorways',      label: 'Wide doorways / turning circle (≥ 900mm)' },
  { key: 'hearing_loop',       label: 'Hearing loop / visual fire alarm' },
  { key: 'accessible_parking', label: 'Accessible parking at property' },
  { key: 'carer_bed',          label: 'Carer / attendant bed in same room' },
  { key: 'adjustable_bed',     label: 'Height-adjustable or profiling bed' },
  { key: 'no_steps',           label: 'No steps at entry / throughout property' },
];

const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500';
const lbl = 'block text-xs font-medium text-gray-700 mb-1';

const NUITEE_BASE = 'https://api.liteapi.travel/v3.0';

// Use Nominatim (OpenStreetMap) to resolve a city name to its ISO country code.
// Returns uppercase ISO 3166-1 alpha-2 code (e.g. 'AU', 'US') or null.
async function detectCountryCode(cityName) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityName)}&format=json&addressdetails=1&limit=1`,
      { headers: { 'Accept-Language': 'en', 'User-Agent': 'STX-Corporate/1.0' } },
    );
    const data = await res.json();
    const code = data?.[0]?.address?.country_code;
    return code ? code.toUpperCase() : null;
  } catch {
    return null;
  }
}

function HotelNameInput({ propertyName, hotelId, searchCity, onUpdate }) {
  const [query,   setQuery]   = useState(propertyName || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(false);
  const debounceRef    = useRef(null);
  const containerRef   = useRef(null);
  // Cache Nominatim country lookups per city to avoid repeat fetches
  const countryCache   = useRef({});

  useEffect(() => {
    function onMouseDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  // Sync when parent resets the field (e.g. Nuitee booking auto-fill)
  useEffect(() => { setQuery(propertyName || ''); }, [propertyName]);

  // Clear stale dropdown when the search city changes
  useEffect(() => { setResults([]); setOpen(false); }, [searchCity]);

  function doSearch(text) {
    clearTimeout(debounceRef.current);
    if (!text || text.length < 3) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const apiKey = process.env.REACT_APP_NUITEE_API_KEY || '';

        const fetchHotels = async (params) => {
          const res = await fetch(`${NUITEE_BASE}/data/hotels?${new URLSearchParams(params)}`, {
            headers: { 'X-API-Key': apiKey, accept: 'application/json' },
          });
          const json = await res.json();
          return Array.isArray(json?.data) ? json.data : [];
        };

        let hotels = [];

        if (searchCity) {
          // Resolve country code for this city via Nominatim (cached after first lookup)
          if (countryCache.current[searchCity] === undefined) {
            countryCache.current[searchCity] = await detectCountryCode(searchCity) || 'AU';
          }
          const countryCode = countryCache.current[searchCity];
          hotels = await fetchHotels({ cityName: searchCity, countryCode, hotelName: text, limit: '8' });
        } else {
          // No city context — fall back to AU
          hotels = await fetchHotels({ hotelName: text, countryCode: 'AU', limit: '8' });
        }

        setResults(hotels.filter(h => h.id).slice(0, 8));
        setOpen(hotels.length > 0);
      } catch {
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 350);
  }

  function handleChange(e) {
    const text = e.target.value;
    setQuery(text);
    onUpdate({ propertyName: text, hotelId: null });
    doSearch(text);
  }

  function handleSelect(hotel) {
    setQuery(hotel.name);
    setOpen(false);
    setResults([]);
    onUpdate({ propertyName: hotel.name, hotelId: hotel.id });
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          className={`${inp} ${hotelId ? 'pr-7' : ''}`}
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Start typing to search, or enter manually"
          autoComplete="off"
          aria-label="Property name"
          aria-autocomplete="list"
        />
        {loading && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <svg className="animate-spin h-3.5 w-3.5 text-gray-400" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        )}
        {hotelId && !loading && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2" title="Name verified via Nuitee">
            <CheckCircle size={13} className="text-teal-500" aria-hidden="true" />
          </div>
        )}
      </div>

      {hotelId && (
        <p className="text-[10px] text-teal-600 mt-0.5 flex items-center gap-1">
          <CheckCircle size={9} aria-hidden="true" />
          Name verified via Nuitee hotel data
        </p>
      )}

      {open && results.length > 0 && (
        <ul
          role="listbox"
          aria-label="Hotel suggestions"
          className="absolute z-20 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto"
        >
          {results.map(hotel => (
            <li key={hotel.id} role="option" aria-selected={false}>
              <button
                type="button"
                onMouseDown={() => handleSelect(hotel)}
                className="w-full text-left px-3 py-2.5 hover:bg-teal-50 border-b border-gray-100 last:border-0 transition-colors"
              >
                <p className="text-sm font-medium text-gray-900 truncate">{hotel.name}</p>
                {/* LiteAPI returns city/country as flat fields — `address` is the street line. */}
                {(hotel.city || hotel.country) && (
                  <p className="text-xs text-gray-600 truncate">
                    {[hotel.city, hotel.country].filter(Boolean).join(', ')}
                  </p>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function F({ label, children, span2 }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <label className={lbl}>{label}</label>
      {children}
    </div>
  );
}

// ── Sector field components ───────────────────────────────────────────────────

function FlightFields({ s, upd, updMany, onOpenFlightBooking, flightMode, flightBookingLocked }) {
  const [ssrOpen, setSsrOpen] = useState((s.specialAssistance || []).length > 0);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <F label="From (airport / city)">
        <input className={inp} value={s.from || ''} onChange={e => upd('from', e.target.value)} placeholder="SYD" />
      </F>
      <F label="To (airport / city)">
        <input className={inp} value={s.to || ''} onChange={e => upd('to', e.target.value)} placeholder="MEL" />
      </F>
      <F label="Date">
        <input type="date" className={inp} value={s.date || ''} onChange={e => upd('date', e.target.value)} />
      </F>
      <F label="Airline">
        <input className={inp} value={s.airline || ''} onChange={e => upd('airline', e.target.value)} placeholder="Qantas" />
      </F>
      <F label="Flight number">
        <input className={inp} value={s.flightNumber || ''} onChange={e => upd('flightNumber', e.target.value)} placeholder="QF403" />
      </F>
      <F label="Booking reference">
        <input className={inp} value={s.bookingRef || ''} onChange={e => upd('bookingRef', e.target.value)} placeholder="ABC123" />
      </F>

      {/* Nuitee flight launcher — ticket outright, or save a fare for approval first */}
      {onOpenFlightBooking && (
        <div className="col-span-2 space-y-2">
          {hasPendingFlightOption(s) && (
            <ProposedFlightCard
              option={s.proposedFlight}
              approved={flightMode === 'book'}
              actions={
                <button
                  type="button"
                  onClick={() => updMany({
                    // Clear what the option wrote but keep the date — it feeds the trip's derived
                    // start/end and is not the option's to take away.
                    proposedFlight: null,
                    airline: '', flightNumber: '', cost: '',
                    departureTime: '', arrivalTime: '', baggageAllowance: '',
                  })}
                  className="px-2.5 py-1 border border-amber-400 text-amber-900 text-xs font-medium rounded-md hover:bg-amber-100"
                >
                  Remove option
                </button>
              }
            />
          )}
          <div>
            <button
              type="button"
              onClick={flightBookingLocked ? undefined : onOpenFlightBooking}
              disabled={flightBookingLocked}
              title={flightBookingLocked
                ? 'Flight booking is handled by STX — ask your STX contact to book this sector'
                : undefined}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-colors ${
                flightBookingLocked
                  ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                  : 'bg-sky-600 text-white hover:bg-sky-700'
              }`}
            >
              <Plane size={13} aria-hidden="true" />
              {flightMode === 'quote'
                ? (hasPendingFlightOption(s) ? 'Change saved flight option' : 'Search & save flight option')
                : (hasPendingFlightOption(s) ? 'Refresh fares & book saved option' : 'Search & Book via Nuitee')}
              {!flightBookingLocked && <ExternalLink size={11} aria-hidden="true" className="opacity-70" />}
            </button>
            <p className="text-[10px] text-gray-600 mt-1">
              {flightBookingLocked
                ? 'Flight booking is handled by STX. Ask your STX contact to book or change this sector.'
                : flightMode === 'quote'
                  ? 'This trip needs approval first, so the flight search saves your chosen fare and price to this sector — it does not ticket it.'
                  : hasPendingFlightOption(s)
                    ? 'Fares expire in minutes, so this re-prices the saved option live before ticketing.'
                    : 'Opens flight search in a new tab — flight details will auto-fill here.'}
            </p>
          </div>
        </div>
      )}
      <F label="Departure time">
        <input type="time" className={inp} value={s.departureTime || ''} onChange={e => upd('departureTime', e.target.value)} />
      </F>
      <F label="Arrival time">
        <input type="time" className={inp} value={s.arrivalTime || ''} onChange={e => upd('arrivalTime', e.target.value)} />
      </F>
      <F label="Cabin class">
        <select className={inp} aria-label="Cabin class" value={s.cabinClass || 'Economy'} onChange={e => upd('cabinClass', e.target.value)}>
          {CABIN_CLASSES.map(c => <option key={c}>{c}</option>)}
        </select>
      </F>
      <F label="Baggage allowance">
        <input className={inp} value={s.baggageAllowance || ''} onChange={e => upd('baggageAllowance', e.target.value)} placeholder="e.g. 23kg / 1 piece" />
      </F>
      <F label="Cost incl. GST (AUD)">
        <input type="number" min="0" step="0.01" className={inp} value={s.cost || ''} onChange={e => upd('cost', e.target.value)} placeholder="0.00" />
      </F>
      <F label="Notes / special requirements" span2>
        <textarea className={inp} rows={2} value={s.notes || ''} onChange={e => upd('notes', e.target.value)} placeholder="e.g. wheelchair assistance, aisle seat required" />
      </F>

      <div className="col-span-2 border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={() => setSsrOpen(v => !v)}
          className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-800 w-full text-left"
        >
          {ssrOpen ? <ChevronUp size={12} aria-hidden="true" /> : <ChevronDown size={12} aria-hidden="true" />}
          <span>Special assistance (SSR codes)</span>
          {(s.specialAssistance || []).length > 0 && (
            <span className="ml-2 flex flex-wrap gap-1">
              {(s.specialAssistance || []).map(c => (
                <span key={c} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">{c}</span>
              ))}
            </span>
          )}
        </button>
        {ssrOpen && (
          <div className="mt-2 space-y-1.5 pl-4">
            {FLIGHT_SSR_OPTIONS.map(opt => (
              <label key={opt.code} className="flex items-start gap-2 text-xs cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={(s.specialAssistance || []).includes(opt.code)}
                  onChange={e => {
                    const cur = s.specialAssistance || [];
                    upd('specialAssistance', e.target.checked ? [...cur, opt.code] : cur.filter(c => c !== opt.code));
                  }}
                  className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">
                  {!opt.other && <span className="font-mono font-semibold text-blue-700 mr-1">{opt.code}</span>}
                  {opt.label}
                </span>
              </label>
            ))}
            {(s.specialAssistance || []).includes('OTHER') && (
              <input
                className={`${inp} mt-1`}
                value={s.specialAssistanceOther || ''}
                onChange={e => upd('specialAssistanceOther', e.target.value)}
                placeholder="Describe the special assistance required…"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AccommodationFields({ s, upd, updMany, tripDestinationCity, onOpenHotelBooking, hotelMode }) {
  const [accomAccessOpen, setAccomAccessOpen] = useState((s.accessibilityRequirements || []).length > 0);
  const nights = s.checkIn && s.checkOut
    ? Math.max(0, Math.round((new Date(s.checkOut) - new Date(s.checkIn)) / 86400000))
    : null;

  // "Hotel is in a different city" checkbox — true when a real reporting city is set. A legacy
  // sector whose reportingCity is just the hotel's name reads as no override at all, so it neither
  // shows a hotel name in a city field nor scopes the hotel search to a property name.
  const reportingCity   = sectorReportingCity(s);
  const isDifferentCity = !!reportingCity;

  // The city used for hotel name search: custom override city, else trip destination
  const searchCity = reportingCity || tripDestinationCity || '';

  function handleDifferentCityToggle(checked) {
    if (checked) {
      // Pre-fill the input with trip destination so user can edit from there
      updMany({ reportingCity: tripDestinationCity || '', hotelId: null });
    } else {
      updMany({ reportingCity: '', hotelId: null });
    }
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {/* Different-city checkbox sits above property name search */}
      <div className="col-span-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isDifferentCity}
            onChange={e => handleDifferentCityToggle(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-gray-700 font-medium">Hotel is in a different city to the trip destination</span>
        </label>
        {isDifferentCity && (
          <div className="mt-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Hotel city <span className="text-gray-600 font-normal">(used for search and spend reporting)</span>
            </label>
            <input
              className={inp}
              list="trip-form-cities"
              value={reportingCity}
              onChange={e => updMany({ reportingCity: e.target.value, hotelId: null })}
              placeholder="e.g. Canberra"
              autoComplete="off"
            />
          </div>
        )}
        {!isDifferentCity && (
          <p className="text-xs text-gray-600 mt-1 ml-6">
            {tripDestinationCity
              ? <>Searching hotels in: <strong className="text-gray-700">{tripDestinationCity}</strong></>
              : 'Set a trip destination above to auto-scope the hotel search.'}
          </p>
        )}
      </div>

      <F label="Property name" span2>
        <HotelNameInput
          propertyName={s.propertyName || ''}
          hotelId={s.hotelId || null}
          searchCity={searchCity}
          onUpdate={updMany}
        />
      </F>
      <F label="Check-in date">
        <input type="date" className={inp} value={s.checkIn || ''} onChange={e => upd('checkIn', e.target.value)} />
      </F>
      <F label="Check-out date">
        <input type="date" className={inp} value={s.checkOut || ''} onChange={e => upd('checkOut', e.target.value)} />
      </F>
      <F label="Room type">
        <input className={inp} value={s.roomType || ''} onChange={e => upd('roomType', e.target.value)} placeholder="Accessible King Room" />
      </F>
      <F label={`Total cost of stay incl. GST (AUD)${nights != null ? ` · ${nights} night${nights !== 1 ? 's' : ''}` : ''}`}>
        <input type="number" min="0" step="0.01" className={inp} value={s.cost || ''} onChange={e => upd('cost', e.target.value)} placeholder="0.00" />
      </F>
      <F label="Booking reference" span2>
        <input className={inp} value={s.bookingRef || ''} onChange={e => upd('bookingRef', e.target.value)} placeholder="BK12345" />
      </F>
      <F label="Inclusions" span2>
        <input className={inp} value={s.inclusions || ''} onChange={e => upd('inclusions', e.target.value)} placeholder="e.g. Breakfast, parking, Wi-Fi" />
      </F>
      <F label="Notes / special requirements" span2>
        <textarea className={inp} rows={2} value={s.notes || ''} onChange={e => upd('notes', e.target.value)} placeholder="e.g. roll-in shower, ground floor, carer room required" />
      </F>

      {/* Nuitee hotel launcher — books outright, or saves an option for approval first */}
      {onOpenHotelBooking && (
        <div className="col-span-2 space-y-2">
          {hasPendingOption(s) && (
            <ProposedHotelCard
              option={s.proposedHotel}
              approved={hotelMode === 'book'}
              actions={
                <button
                  type="button"
                  onClick={() => updMany({
                    // Clear the fields the option wrote, but keep the dates — they feed the trip's
                    // derived start/end and are not the option's to take away.
                    proposedHotel: null,
                    propertyName: '', roomType: '', inclusions: '', cost: '', hotelId: null,
                  })}
                  className="px-2.5 py-1 border border-amber-400 text-amber-900 text-xs font-medium rounded-md hover:bg-amber-100"
                >
                  Remove option
                </button>
              }
            />
          )}
          <div>
            <button
              type="button"
              onClick={onOpenHotelBooking}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Hotel size={13} />
              {hotelMode === 'quote'
                ? (hasPendingOption(s) ? 'Change saved hotel option' : 'Search & save hotel option')
                : (hasPendingOption(s) ? 'Refresh prices & book saved option' : 'Search & Book via Nuitee')}
              <ExternalLink size={11} aria-hidden="true" className="opacity-70" />
            </button>
            <p className="text-[10px] text-gray-600 mt-1">
              {hotelMode === 'quote'
                ? 'This trip needs approval first, so the hotel search saves your chosen room and price to this sector — it does not book it.'
                : hasPendingOption(s)
                  ? 'Re-opens the saved option against live availability so you can confirm the price before booking.'
                  : 'Opens hotel search in a new tab — booking details will auto-fill here.'}
            </p>
          </div>
        </div>
      )}

      <div className="col-span-2 border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={() => setAccomAccessOpen(v => !v)}
          className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-800 w-full text-left"
        >
          {accomAccessOpen ? <ChevronUp size={12} aria-hidden="true" /> : <ChevronDown size={12} aria-hidden="true" />}
          <span>Accessibility requirements</span>
          {(s.accessibilityRequirements || []).length > 0 && (
            <span className="ml-1 text-amber-700 font-semibold">{(s.accessibilityRequirements || []).length} selected</span>
          )}
        </button>
        {accomAccessOpen && (
          <div className="mt-2 space-y-1.5 pl-4">
            <div className="grid grid-cols-2 gap-1.5">
              {ACCOM_ACCESSIBILITY_OPTIONS.map(opt => (
                <label key={opt.key} className="flex items-start gap-2 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={(s.accessibilityRequirements || []).includes(opt.key)}
                    onChange={e => {
                      const cur = s.accessibilityRequirements || [];
                      upd('accessibilityRequirements', e.target.checked ? [...cur, opt.key] : cur.filter(k => k !== opt.key));
                    }}
                    className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Additional notes</label>
              <textarea
                className={inp}
                rows={2}
                value={s.accessibilityNotes || ''}
                onChange={e => upd('accessibilityNotes', e.target.value)}
                placeholder="Any other accessibility requirements for this property…"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CarHireFields({ s, upd }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <F label="Company" span2>
        <input className={inp} value={s.company || ''} onChange={e => upd('company', e.target.value)} placeholder="Hertz" />
      </F>
      <F label="Pickup location">
        <input className={inp} value={s.pickupLocation || ''} onChange={e => upd('pickupLocation', e.target.value)} placeholder="Sydney Airport" />
      </F>
      <F label="Drop-off location">
        <input className={inp} value={s.dropOffLocation || ''} onChange={e => upd('dropOffLocation', e.target.value)} placeholder="Melbourne CBD" />
      </F>
      <F label="Pickup date">
        <input type="date" className={inp} value={s.pickupDate || ''} onChange={e => upd('pickupDate', e.target.value)} />
      </F>
      <F label="Drop-off date">
        <input type="date" className={inp} value={s.dropOffDate || ''} onChange={e => upd('dropOffDate', e.target.value)} />
      </F>
      <F label="Vehicle type">
        <input className={inp} value={s.vehicleType || ''} onChange={e => upd('vehicleType', e.target.value)} placeholder="Accessible Sedan" />
      </F>
      <F label="Booking reference">
        <input className={inp} value={s.bookingRef || ''} onChange={e => upd('bookingRef', e.target.value)} />
      </F>
      <F label="Cost incl. GST (AUD)">
        <input type="number" min="0" step="0.01" className={inp} value={s.cost || ''} onChange={e => upd('cost', e.target.value)} placeholder="0.00" />
      </F>
      <F label="Inclusions" span2>
        <input className={inp} value={s.inclusions || ''} onChange={e => upd('inclusions', e.target.value)} placeholder="e.g. Unlimited km, insurance, GPS" />
      </F>
      <F label="Notes / special requirements" span2>
        <textarea className={inp} rows={2} value={s.notes || ''} onChange={e => upd('notes', e.target.value)} />
      </F>
    </div>
  );
}

function ParkingFields({ s, upd }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <F label="Facility" span2>
        <input className={inp} value={s.facility || ''} onChange={e => upd('facility', e.target.value)} placeholder="Wilson Parking — Central Station" />
      </F>
      <F label="Entry date">
        <input type="date" className={inp} value={s.entryDate || ''} onChange={e => upd('entryDate', e.target.value)} />
      </F>
      <F label="Exit date">
        <input type="date" className={inp} value={s.exitDate || ''} onChange={e => upd('exitDate', e.target.value)} />
      </F>
      <F label="Booking reference">
        <input className={inp} value={s.bookingRef || ''} onChange={e => upd('bookingRef', e.target.value)} />
      </F>
      <F label="Cost incl. GST (AUD)">
        <input type="number" min="0" step="0.01" className={inp} value={s.cost || ''} onChange={e => upd('cost', e.target.value)} placeholder="0.00" />
      </F>
      <F label="Notes" span2>
        <textarea className={inp} rows={2} value={s.notes || ''} onChange={e => upd('notes', e.target.value)} />
      </F>
    </div>
  );
}

function TransfersFields({ s, upd }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <F label="From">
        <input className={inp} value={s.from || ''} onChange={e => upd('from', e.target.value)} placeholder="Airport" />
      </F>
      <F label="To">
        <input className={inp} value={s.to || ''} onChange={e => upd('to', e.target.value)} placeholder="Hotel" />
      </F>
      <F label="Date">
        <input type="date" className={inp} value={s.date || ''} onChange={e => upd('date', e.target.value)} />
      </F>
      <F label="Pickup time">
        <input type="time" className={inp} value={s.pickupTime || ''} onChange={e => upd('pickupTime', e.target.value)} />
      </F>
      <F label="Transfer type">
        <select className={inp} aria-label="Transfer type" value={s.transferType || ''} onChange={e => upd('transferType', e.target.value)}>
          <option value="">Select…</option>
          {TRANSFER_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
      </F>
      <F label="Provider">
        <input className={inp} value={s.provider || ''} onChange={e => upd('provider', e.target.value)} placeholder="Silver Service" />
      </F>
      <F label="Cost incl. GST (AUD)">
        <input type="number" min="0" step="0.01" className={inp} value={s.cost || ''} onChange={e => upd('cost', e.target.value)} placeholder="0.00" />
      </F>
      <F label="Notes / special requirements" span2>
        <textarea className={inp} rows={2} value={s.notes || ''} onChange={e => upd('notes', e.target.value)} placeholder="e.g. wheelchair accessible vehicle required" />
      </F>
    </div>
  );
}

function MealsFields({ s, upd }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <F label="Venue" span2>
        <input className={inp} value={s.venue || ''} onChange={e => upd('venue', e.target.value)} placeholder="Restaurant / event name" />
      </F>
      <F label="Date">
        <input type="date" className={inp} value={s.date || ''} onChange={e => upd('date', e.target.value)} />
      </F>
      <F label="Meal type">
        <select className={inp} aria-label="Meal type" value={s.mealType || ''} onChange={e => upd('mealType', e.target.value)}>
          <option value="">Select…</option>
          {MEAL_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
      </F>
      <F label="Number of people">
        <input type="number" min="1" className={inp} value={s.numberOfPeople || ''} onChange={e => upd('numberOfPeople', e.target.value)} placeholder="1" />
      </F>
      <F label="Cost incl. GST (AUD)">
        <input type="number" min="0" step="0.01" className={inp} value={s.cost || ''} onChange={e => upd('cost', e.target.value)} placeholder="0.00" />
      </F>
      <F label="Notes / dietary requirements" span2>
        <textarea className={inp} rows={2} value={s.notes || ''} onChange={e => upd('notes', e.target.value)} />
      </F>
    </div>
  );
}

function OtherFields({ s, upd }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <F label="Description" span2>
        <input className={inp} value={s.description || ''} onChange={e => upd('description', e.target.value)} placeholder="e.g. Travel insurance, visa fees, equipment hire" />
      </F>
      <F label="Provider">
        <input className={inp} value={s.provider || ''} onChange={e => upd('provider', e.target.value)} />
      </F>
      <F label="Date">
        <input type="date" className={inp} value={s.date || ''} onChange={e => upd('date', e.target.value)} />
      </F>
      <F label="Cost incl. GST (AUD)">
        <input type="number" min="0" step="0.01" className={inp} value={s.cost || ''} onChange={e => upd('cost', e.target.value)} placeholder="0.00" />
      </F>
      <F label="Notes" span2>
        <textarea className={inp} rows={2} value={s.notes || ''} onChange={e => upd('notes', e.target.value)} />
      </F>
    </div>
  );
}

// ── Sector summary (collapsed view) ──────────────────────────────────────────

function sectorSummary(s) {
  if (s.type === 'flight')        return [s.from, s.to].filter(Boolean).join(' → ') || 'Flight';
  if (s.type === 'accommodation') return s.propertyName || 'Accommodation';
  if (s.type === 'car-hire')      return [s.company, s.pickupLocation].filter(Boolean).join(' · ') || 'Car Hire';
  if (s.type === 'parking')       return s.facility || 'Parking';
  if (s.type === 'transfers')     return [s.from, s.to].filter(Boolean).join(' → ') || 'Transfer';
  if (s.type === 'meals')         return s.venue || s.mealType || 'Meal';
  return s.description || 'Other';
}

// ── Sector card ───────────────────────────────────────────────────────────────

function SectorCard({ sector, index, onChange, onRemove, tripDestinationCity, onOpenHotelBooking, hotelMode, onOpenFlightBooking, flightMode, flightBookingLocked }) {
  const [expanded, setExpanded] = useState(!sector.type);
  const cfg = SECTOR_TYPES[sector.type];
  const Icon = cfg?.Icon;

  const upd     = (k, v)    => onChange({ ...sector, [k]: v });
  const updMany = (updates) => onChange({ ...sector, ...updates });

  const fields = {
    flight:        <FlightFields s={sector} upd={upd} updMany={updMany} onOpenFlightBooking={onOpenFlightBooking} flightMode={flightMode} flightBookingLocked={flightBookingLocked} />,
    accommodation: <AccommodationFields s={sector} upd={upd} updMany={updMany} tripDestinationCity={tripDestinationCity} onOpenHotelBooking={onOpenHotelBooking} hotelMode={hotelMode} />,
    'car-hire':    <CarHireFields s={sector} upd={upd} />,
    parking:       <ParkingFields s={sector} upd={upd} />,
    transfers:     <TransfersFields s={sector} upd={upd} />,
    meals:         <MealsFields s={sector} upd={upd} />,
    other:         <OtherFields s={sector} upd={upd} />,
  }[sector.type];

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50">
        <span className="text-xs font-medium text-gray-600 w-5 shrink-0">{index + 1}</span>

        {!sector.type ? (
          <select
            className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
            aria-label="Sector type"
            value=""
            onChange={e => { onChange({ ...sector, type: e.target.value }); setExpanded(true); }}
          >
            <option value="" disabled>Select sector type…</option>
            {Object.entries(SECTOR_TYPES).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        ) : (
          <>
            {Icon && <Icon size={14} aria-hidden="true" className="text-gray-600 shrink-0" />}
            <span className="text-xs font-semibold text-gray-700 shrink-0 w-24">{cfg.label}</span>
            <span className="flex-1 text-xs text-gray-700 truncate">{sectorSummary(sector)}</span>
            {sector.cost && (
              <span className="text-xs text-gray-700 shrink-0 mr-1">
                A${parseFloat(sector.cost || 0).toFixed(2)}
              </span>
            )}
          </>
        )}

        <div className="flex items-center gap-0.5 shrink-0">
          {sector.type && (
            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              aria-label={expanded ? 'Collapse sector' : 'Expand sector'}
              className="p-1 text-gray-600 hover:text-gray-600 rounded"
            >
              {expanded ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove sector"
            className="p-1 text-gray-600 hover:text-red-500 rounded"
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        </div>
      </div>

      {expanded && sector.type && (
        <div className="px-4 py-4 border-t border-gray-100 space-y-4">
          {fields}
          <div className="pt-2 border-t border-gray-100">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sector.international || false}
                onChange={e => onChange({ ...sector, international: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700 font-medium">International sector</span>
              <span className="text-xs text-gray-600">(GST-free — no GST applied on invoice)</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Additional passenger card ─────────────────────────────────────────────────

function AdditionalPassengerCard({ pax, index, sectors, passengers, teamMembers, costCentres, autoAllocated, onChange, onRemove }) {
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Passenger {index + 2}</span>
        <button type="button" onClick={onRemove} aria-label="Remove passenger" className="p-1 text-gray-600 hover:text-red-500 rounded">
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Name *</label>
          <input
            className={inp}
            value={pax.name}
            onChange={e => {
              const name  = e.target.value;
              const lower = normName(name);
              const paxMatch = passengers.find(p => passengerMatchesName(p, lower));
              if (paxMatch) {
                const linkedUser = paxMatch.userId ? teamMembers.find(m => m.id === paxMatch.userId) : null;
                onChange({ ...pax, name, passengerId: paxMatch.id, costCentre: linkedUser?.costCentre || pax.costCentre });
              } else {
                const memMatch = teamMembers.find(m =>
                  normName([m.firstName, m.lastName].filter(Boolean).join(' ')) === lower
                );
                onChange({ ...pax, name, passengerId: memMatch ? memMatch.id : '' });
              }
            }}
            placeholder="Type name…"
            list={`travellers-add-${pax._key}`}
            autoComplete="off"
          />
          <datalist id={`travellers-add-${pax._key}`}>
            {passengers.map(p => (
              <option key={p.id} value={[p.preferredName || p.firstName, p.lastName].filter(Boolean).join(' ')} />
            ))}
            {teamMembers.filter(m => !passengers.some(p => p.userId === m.id)).map(m => (
              <option key={m.id} value={[m.firstName, m.lastName].filter(Boolean).join(' ')} />
            ))}
          </datalist>
        </div>

        {costCentres.length > 0 ? (
          <div>
            <label className={lbl}>Cost centre</label>
            <select className={inp} aria-label="Passenger cost centre" value={pax.costCentre || ''} onChange={e => onChange({ ...pax, costCentre: e.target.value })}>
              <option value="">Select…</option>
              {costCentres.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label className={lbl}>Cost centre</label>
            <input className={inp} value={pax.costCentre || ''} onChange={e => onChange({ ...pax, costCentre: e.target.value })} placeholder="Optional" />
          </div>
        )}
        <div>
          <label className={lbl}>Role</label>
          <select className={inp} aria-label="Passenger role" value={pax.role || 'traveller'} onChange={e => onChange({ ...pax, role: e.target.value })}>
            <option value="traveller">Co-traveller</option>
            <option value="support_worker">Support worker</option>
          </select>
        </div>
      </div>

      {sectors.some(s => s.type) && (
        <div>
          <label className={lbl}>Sectors this passenger is on</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {sectors.map(s => {
              if (!s.type) return null;
              const sLabel = SECTOR_TYPES[s.type]?.label || s.type;
              const cost   = parseFloat(s.cost) || 0;
              const checked = (pax.sectorKeys || []).includes(s._key);
              return (
                <label
                  key={s._key}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs cursor-pointer select-none transition-colors ${
                    checked ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={e => {
                      const newKeys = e.target.checked
                        ? [...(pax.sectorKeys || []), s._key]
                        : (pax.sectorKeys || []).filter(k => k !== s._key);
                      onChange({ ...pax, sectorKeys: newKeys, allocatedCostOverride: false });
                    }}
                    className="w-3 h-3 accent-blue-600"
                  />
                  {sLabel}{cost > 0 ? ` ($${cost.toFixed(0)})` : ''}
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <label className={lbl}>Allocated cost incl. GST (A$)</label>
        <div className="flex items-center gap-2">
          <input
            type="number" min="0" step="0.01"
            className={`${inp} w-36`}
            value={pax.allocatedCostOverride ? (pax.allocatedCost ?? '') : autoAllocated.toFixed(2)}
            onChange={e => onChange({ ...pax, allocatedCost: e.target.value, allocatedCostOverride: true })}
          />
          {pax.allocatedCostOverride ? (
            <button
              type="button"
              onClick={() => onChange({ ...pax, allocatedCostOverride: false, allocatedCost: '' })}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Reset to auto
            </button>
          ) : (
            <span className="text-xs text-gray-600">Auto from sector shares</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main TripForm ─────────────────────────────────────────────────────────────

const EMPTY = {
  clientId: '', title: '', travellerName: '', travellerId: '', tripType: '', costCentre: '',
  originCity: '', destinationCity: '',
  purpose: '', startDate: '', endDate: '', internalNotes: '', sectors: [],
  costCentreChangeReason: '', vtoTripId: '', digitalItineraryLink: '',
  additionalPassengers: [],
  primaryAllocatedCost: '', primaryAllocatedCostOverride: false,
  travelInsurance: null,
  nominatedApproverId: '', nominatedApproverName: '',
};


/**
 * Attach a booking's return leg to the sector at `sectorIndex`, replacing any return leg already
 * attached to it.
 *
 * Keyed to a stable `flightGroupId` on the outbound sector, NOT to the offer id: offer ids are
 * regenerated by every search, so re-pricing a saved option used to leave the old return sector
 * behind and add a second one. Also clears a stale return leg when a return booking is replaced
 * by a one-way.
 */
function applyReturnSector(sectors, sectorIndex, returnSector) {
  const out = [...sectors];
  const groupId = out[sectorIndex].flightGroupId || `fg_${Math.random().toString(36).slice(2, 10)}`;
  out[sectorIndex] = { ...out[sectorIndex], flightGroupId: groupId };

  // Drop whatever return leg this outbound previously owned. Legacy sectors written before
  // flightGroupId existed are matched on the route and date they duplicate, and only ever when
  // they are already flagged as a return leg — never a sector someone entered by hand.
  const stale = (x, i) => i !== sectorIndex && x.returnLegOf && (
    x.returnLegOf === groupId ||
    (returnSector && x.from === returnSector.from && x.to === returnSector.to && x.date === returnSector.date)
  );
  const cleaned = out.filter((x, i) => !stale(x, i));
  const idx = cleaned.findIndex(x => x.flightGroupId === groupId);

  if (!returnSector) return cleaned;
  cleaned.splice(idx + 1, 0, { ...returnSector, returnLegOf: groupId });
  return cleaned;
}

const MANAGER_ROLES = ['stx_admin', 'stx_ops', 'client_ops', 'client_approver'];

export default function TripForm({ trip, clientId: clientIdProp, onSave, onCancel }) {
  const { userProfile } = useAuth();
  const { clientConfig: tenantClientConfig, isSTX } = useTenant();

  // STX users have no own tenant config — load whichever client is selected in the form
  const [stxClientConfig, setStxClientConfig] = useState(null);
  const clientConfig = isSTX ? stxClientConfig : tenantClientConfig;
  // Baseline cost centre — saved value for existing trips, updated from traveller auto-fill for new trips
  const [originalCostCentre, setOriginalCostCentre] = useState(trip?.costCentre || '');
  const [clients, setClients]         = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [passengers, setPassengers]   = useState([]);

  // Load client list for STX users
  useEffect(() => {
    if (!isSTX) return;
    const unsub = onSnapshot(collection(db, 'clients'), snap => {
      setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    });
    return unsub;
  }, [isSTX]);

  const [form, setForm] = useState(() => {
    // Auto-fill travellerName + travellerId + costCentre for non-manager users creating their own trip
    let autoName = '';
    let autoId   = '';
    let autoCostCentre = '';
    if (!trip && userProfile && !MANAGER_ROLES.includes(userProfile.role)) {
      autoName       = [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ');
      autoId         = userProfile.uid || '';
      autoCostCentre = userProfile.costCentre || '';
    }
    if (!trip) return { ...EMPTY, clientId: clientIdProp || '', travellerName: autoName, travellerId: autoId, costCentre: autoCostCentre };
    const sectorList = (trip.sectors || []).map(s => ({ ...s, _key: Math.random().toString(36).slice(2) }));
    return {
      clientId:        trip.clientId        || clientIdProp || '',
      title:           trip.title           || '',
      travellerName:   trip.travellerName   || '',
      travellerId:     trip.travellerId     || '',
      tripType:        trip.tripType        || '',
      costCentre:      trip.costCentre      || '',
      originCity:      trip.originCity      || '',
      destinationCity: trip.destinationCity || '',
      purpose:         trip.purpose         || '',
      startDate:       trip.startDate       || '',
      endDate:         trip.endDate         || '',
      internalNotes:         trip.internalNotes         || '',
      vtoTripId:             trip.vtoTripId             || '',
      digitalItineraryLink:  trip.digitalItineraryLink  || '',
      sectors: sectorList,
      additionalPassengers: (trip.additionalPassengers || []).map(p => ({
        ...p,
        _key: Math.random().toString(36).slice(2),
        sectorKeys: (p.sectorIndices || []).map(i => sectorList[i]?._key).filter(Boolean),
      })),
      primaryAllocatedCost:         trip.primaryAllocatedCost != null ? String(trip.primaryAllocatedCost) : '',
      primaryAllocatedCostOverride: trip.primaryAllocatedCost != null,
      travelInsurance:              trip.travelInsurance ?? null,
      nominatedApproverId:          trip.nominatedApproverId   || '',
      nominatedApproverName:        trip.nominatedApproverName || '',
    };
  });

  // Load selected client's config for STX users (clientConfig from useTenant is always null for STX)
  useEffect(() => {
    if (!isSTX || !form.clientId) { setStxClientConfig(null); return; }
    getDoc(doc(db, 'clients', form.clientId, 'config', 'settings')).then(snap => {
      setStxClientConfig(snap.exists() ? snap.data() : {});
    }).catch(() => setStxClientConfig({}));
  }, [isSTX, form.clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load team members and passenger profiles for traveller autocomplete
  useEffect(() => {
    const cid = form.clientId || clientIdProp;
    if (!cid) return;
    const qUsers = query(collection(db, 'users'), where('clientId', '==', cid));
    const qPax   = query(collection(db, 'clients', cid, 'passengers'), orderBy('lastName'));
    const u1 = onSnapshot(qUsers, snap =>
      setTeamMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(m => m.active !== false))
    );
    const u2 = onSnapshot(qPax, snap =>
      setPassengers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { u1(); u2(); };
  }, [form.clientId, clientIdProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [travelPolicy, setTravelPolicy] = useState({ rates: {}, flightRates: {} });

  // Load client travel policy for variance checking
  useEffect(() => {
    const cid = form.clientId || clientIdProp;
    if (!cid) return;
    getDoc(doc(db, 'clients', cid, 'config', 'travelPolicy')).then(snap => {
      if (snap.exists()) setTravelPolicy(snap.data());
      else setTravelPolicy({ rates: {}, flightRates: {} });
    }).catch(() => {});
  }, [form.clientId, clientIdProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // The profile behind the traveller name — drives the legal-name warning, the accessibility card
  // and the guest email sent to the hotel flow.
  //
  // travellerId is only trusted while it agrees with the name in the field. It used to win
  // outright, so a stale id — carried in from an edited trip, or left over from an earlier
  // selection — surfaced an entirely different traveller's details against the typed name
  // (a "Martin Neville Butcher" trip warning about Emily Maree Maiorino's preferred name).
  // A profile that contradicts the typed name is worse than no match, so it resolves to null.
  // The flight-result handler below is registered once, so it cannot close over these: it would
  // capture the first render, when the passenger list is still empty and nothing matches. A ref
  // refreshed each render gives it the current values without re-registering the listener.
  const passengerCtxRef = useRef({ profiles: [], travellerProfile: null });

  // Shared with TripDetail — see resolveTravellerProfile. The two screens open the same booking
  // flow, so a profile found by one and missed by the other produces a ticket in the wrong name
  // depending on which button you came in through.
  const matchedPassenger = useMemo(
    () => resolveTravellerProfile(
      { travellerName: form.travellerName, travellerId: form.travellerId }, passengers),
    [passengers, form.travellerId, form.travellerName]);

  useEffect(() => {
    passengerCtxRef.current = { profiles: passengers, travellerProfile: matchedPassenger };
  });

  const tripTypes   = clientConfig?.dropdowns?.tripTypes?.length
    ? clientConfig.dropdowns.tripTypes
    : DEFAULT_TRIP_TYPES;
  const costCentres = clientConfig?.dropdowns?.costCentres || [];

  const updateSector = (i, updated) =>
    setForm(p => { const s = [...p.sectors]; s[i] = updated; return { ...p, sectors: s }; });

  const removeSector = (i) => {
    const removedKey = form.sectors[i]?._key;
    setForm(p => ({
      ...p,
      sectors: p.sectors.filter((_, j) => j !== i),
      additionalPassengers: p.additionalPassengers.map(pax => ({
        ...pax,
        sectorKeys: (pax.sectorKeys || []).filter(k => k !== removedKey),
      })),
    }));
  };

  const addSector = () =>
    setForm(p => ({ ...p, sectors: [...p.sectors, { _key: Math.random().toString(36).slice(2), type: '' }] }));

  // ── Hotel booking via Nuitee (new tab + postMessage) ──────────────────────
  const hotelWindowRef = useRef(null);
  const [nuiteeBooked, setNuiteeBooked] = useState(false);
  const [nuiteeOptionSaved, setNuiteeOptionSaved] = useState(null); // propertyName

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      const isBooking = e.data?.type === 'HOTEL_BOOKED';
      const isOption  = e.data?.type === 'HOTEL_OPTION_SAVED';
      if (!isBooking && !isOption) return;
      const { sectorIndex } = e.data;
      const payload = isBooking ? e.data.bookingData : e.data.optionData;
      if (typeof sectorIndex !== 'number' || !payload) return;
      setForm(prev => {
        const sectors = [...prev.sectors];
        const sector = sectors[sectorIndex];
        if (!sector) return prev;
        sectors[sectorIndex] = isBooking
          ? sectorPatchFromBooking(sector, payload)
          : sectorPatchFromOption(sector, payload);
        return { ...prev, sectors };
      });
      if (isBooking) setNuiteeBooked(true);
      else setNuiteeOptionSaved(payload.propertyName || '');
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // ── Flight booking via Nuitee (new tab + postMessage) ─────────────────────
  // Same shape as the hotel flow above, including the quote-vs-book gate: an unapproved trip
  // saves the chosen fare for the approver rather than ticketing it.
  const flightWindowRef = useRef(null);
  const [flightBooked, setFlightBooked] = useState(false);
  const [flightOptionSaved, setFlightOptionSaved] = useState(null); // "QF403 BNE→SYD"

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      const isBooking = e.data?.type === 'FLIGHT_BOOKED';
      const isOption  = e.data?.type === 'FLIGHT_OPTION_SAVED';
      if (!isBooking && !isOption) return;
      const { sectorIndex } = e.data;
      const payload = isBooking ? e.data.bookingData : e.data.optionData;
      if (typeof sectorIndex !== 'number' || !payload) return;
      setForm(prev => {
        const sectors = [...prev.sectors];
        const sector = sectors[sectorIndex];
        if (!sector) return prev;
        sectors[sectorIndex] = isBooking
          ? sectorPatchFromFlightBooking(sector, payload)
          : sectorPatchFromFlightOption(sector, payload);

        // A return is one fare across two flights. The price stays whole on the outbound sector;
        // the return leg gets its own sector so it appears in the itinerary and in the travel-day
        // and flight-number reports, carrying zero cost so the trip total is not doubled.
        const withReturn = applyReturnSector(sectors, sectorIndex, payload.returnSector || null)
          .map(x => (x._key ? x : { ...x, _key: Math.random().toString(36).slice(2) }));

        // Anyone added to the booking who is not already on the trip is added to it, assigned to
        // this sector. Without this the trip claims one traveller while the fare covers two, which
        // then flows into cost allocation and the support worker report as though they never flew.
        // The comparison is deliberately NOT on the ticketed name alone: the trip names people by
        // their preferred name and the ticket carries the legal one, so matching on the string
        // added the traveller to their own trip again. See newBookingPassengers.
        const sectorKey = withReturn[sectorIndex]?._key;
        const { profiles, travellerProfile } = passengerCtxRef.current;
        const added = newBookingPassengers(payload.passengers, {
          travellerName: prev.travellerName,
          travellerProfile,
          additionalPassengers: prev.additionalPassengers,
          profiles,
        })
          .map(p => ({
            _key: Math.random().toString(36).slice(2),
            name: p.name,
            passengerId: p.passengerId || '',
            costCentre: '',
            role: p.role || 'traveller',
            sectorKeys: sectorKey ? [sectorKey] : [],
            allocatedCost: '',
            allocatedCostOverride: false,
          }));

        return {
          ...prev,
          sectors: withReturn,
          additionalPassengers: added.length
            ? [...prev.additionalPassengers, ...added]
            : prev.additionalPassengers,
        };
      });
      const label = [payload.flightNumber, payload.origin && payload.destination
        ? `${payload.origin}→${payload.destination}` : ''].filter(Boolean).join(' ');
      if (isBooking) setFlightBooked(true);
      else setFlightOptionSaved(label);
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const openFlightBooking = (sectorIndex) => {
    const cid = form.clientId || clientIdProp || '';
    const sector = form.sectors[sectorIndex];
    const params = new URLSearchParams({
      tripId:      trip?.id || '',
      sectorIndex: String(sectorIndex),
      clientId:    cid,
      origin:      sector?.from || form.originCity || '',
      destination: sector?.to   || form.destinationCity || '',
      date:        sector?.date || form.startDate || '',
      // The sector's Airline field is free text ('Qantas'), and the rates API has no carrier
      // filter — so it pre-selects the results filter rather than narrowing the request.
      airline:     sector?.airline || '',
      // Assistance cannot be sent to the airline through the API, so the flow shows it and
      // makes the person booking acknowledge that they will arrange it directly.
      ssr:      JSON.stringify(sector?.specialAssistance || []),
      ssrOther: sector?.specialAssistanceOther || '',
      // Frequent flyer numbers from the profile. `program` is free text, so the flow
      // resolves it to an IATA code and lets STX correct it before anything is sent.
      paxLoyalty: JSON.stringify(matchedPassenger?.loyaltyPrograms || []),
      travellerName: form.travellerName || '',
      email:         matchedPassenger?.email || '',
      // Discrete name parts, not a display name: the ticket needs the legal first/last split and
      // splitting a display name on whitespace guesses wrong on multi-word surnames.
      paxFirstName:  matchedPassenger?.firstName || '',
      paxLastName:   matchedPassenger?.lastName  || '',
      paxMiddleName: matchedPassenger?.middleName || '',
      paxDob:        matchedPassenger?.dateOfBirth || '',
      paxGender:     matchedPassenger?.gender || '',
      paxPhone:      matchedPassenger?.phone || '',
      // Identity documents from the profile, so the traveller's passport or licence does not
      // have to be retyped for every booking. Trimmed to what the airline needs.
      paxDocs: JSON.stringify((matchedPassenger?.identityDocuments || [])
        .filter(d => d && (d.number || '').trim())
        .map(d => ({ type: d.type, number: d.number, expiry: d.expiry, notes: d.notes }))),
      // Everyone on this sector — a support worker or carer flies on the same booking, not a
      // separate one, or they can end up on a different fare and not seated together.
      pax: JSON.stringify(
        flightPassengersFor(
          {
            travellerName: form.travellerName,
            // In the form a passenger's sectors are tracked by key; only once saved do they
            // become indices. Resolve against this sector's key rather than converting indices,
            // which would be off by however many untyped rows sit above it.
            additionalPassengers: form.additionalPassengers.map(ap => ({
              ...ap,
              sectorIndices: (ap.sectorKeys || []).length === 0
                ? []                                                   // unassigned = every sector
                : (ap.sectorKeys.includes(sector?._key) ? [sectorIndex] : [-1]),
            })),
          },
          sectorIndex, passengers, matchedPassenger,
        ).map(flightPassengerParam)),
      mode:          flightMode,
      // The booking lands in unsaved form state here, so the confirmation screen tells the
      // booker to press Save rather than implying the trip is already updated.
      openedFrom:    'form',
    });
    if (flightMode === 'book' && hasPendingFlightOption(sector)) {
      params.set('savedOption', encodeSavedFlight(sector.proposedFlight));
    }
    // Bulky context — passengers, documents, loyalty, a saved option — is stashed under a short
    // handle rather than travelling on the query string. See utils/bookingContext for why.
    const url = buildBookingUrl('/flight-booking', params, BOOKING_URL_KEYS);
    if (flightWindowRef.current && !flightWindowRef.current.closed) {
      flightWindowRef.current.focus();
    } else {
      flightWindowRef.current = window.open(url, '_blank', 'width=1200,height=800');
    }
  };

  const openHotelBooking = (sectorIndex) => {
    const cid = form.clientId || clientIdProp || '';
    const sector = form.sectors[sectorIndex];
    const params = new URLSearchParams({
      tripId:          trip?.id || '',
      sectorIndex:     String(sectorIndex),
      tripType:        form.tripType || '',
      clientId:        cid,
      checkin:         sector?.checkIn  || form.startDate || '',
      checkout:        sector?.checkOut || form.endDate   || '',
      travellerName:   form.travellerName || '',
      destinationCity: form.destinationCity || '',
      email:           matchedPassenger?.email || '',
      mode:            hotelMode,
      // The traveller's recorded access needs ride across so the search can pre-select the
      // filters it can actually check, and show the rest as things to confirm with the property.
      accessNeeds:     JSON.stringify(sector?.accessibilityRequirements || []),
      accessNotes:     sector?.accessibilityNotes || '',
    });
    // Booking an already-quoted room: hand the saved option across so the flow can reopen that
    // hotel and show what has changed since it was approved.
    if (hotelMode === 'book' && hasPendingOption(sector)) {
      params.set('savedOption', encodeSavedOption(sector.proposedHotel));
    }
    const url = buildBookingUrl('/hotel-booking', params, BOOKING_URL_KEYS);
    if (hotelWindowRef.current && !hotelWindowRef.current.closed) {
      hotelWindowRef.current.focus();
    } else {
      hotelWindowRef.current = window.open(url, '_blank', 'width=1200,height=800');
    }
  };

  // Gross: what the services cost. Passenger cost allocation splits THIS, not the net figure —
  // a credit is a payment method, not a discount on any particular passenger's travel.
  const totalCost = form.sectors.reduce((sum, s) => sum + (parseFloat(s.cost) || 0), 0);

  // ── Travel credit ───────────────────────────────────────────────────────────
  // Credit already paid by this client on an earlier trip for this passenger. STX only: it
  // reduces what the client is billed, so it isn't the client's own lever to pull.
  const { credits } = useTravelCredits(form.clientId || clientIdProp);

  const creditPassengerId = matchedPassenger?.id || '';
  const availableCredits  = useMemo(
    () => availableCreditsFor(credits, creditPassengerId),
    [credits, creditPassengerId],
  );

  // creditDraft maps creditId → typed amount string, so a half-typed "1" doesn't reset to 0.
  const [creditDraft, setCreditDraft] = useState(() => {
    const init = {};
    for (const c of (trip?.creditsApplied || [])) init[c.creditId] = String(c.amountInclGST ?? '');
    return init;
  });

  // Drop drafts for credits that are no longer offered — changing the traveller must not carry
  // the previous passenger's credit across to someone else's trip.
  useEffect(() => {
    setCreditDraft(prev => {
      const allowed = new Set(availableCredits.map(c => c.id));
      for (const c of (trip?.creditsApplied || [])) allowed.add(c.creditId);
      const next = Object.fromEntries(Object.entries(prev).filter(([id]) => allowed.has(id)));
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [availableCredits, trip]);

  const creditApplications = useMemo(
    () => Object.entries(creditDraft)
      .map(([creditId, v]) => ({ creditId, amountInclGST: parseFloat(v) || 0 }))
      .filter(a => a.amountInclGST > 0),
    [creditDraft],
  );

  const feesInclGST = (trip?.fees || []).reduce(
    (s, f) => f.waived ? s : s + (parseFloat(f.amount) || 0) * (1 + (f.gstRate ?? 0.1)), 0);
  const tripGross      = totalCost + feesInclGST;
  const creditTotal    = creditApplications.reduce((s, a) => s + a.amountInclGST, 0);
  const creditCheck    = validateCreditApplications(creditApplications, credits, tripGross);
  const netTripTotal   = Math.max(0, tripGross - creditTotal);

  // Equal-split allocation across all passengers
  const allocation = useMemo(() => {
    const numPax = 1 + (form.additionalPassengers || []).length;
    const autoPerPax = numPax > 1 ? totalCost / numPax : totalCost;

    const primaryDisplayed = form.primaryAllocatedCostOverride
      ? (parseFloat(form.primaryAllocatedCost) || 0)
      : autoPerPax;

    const addPaxDisplayed = (form.additionalPassengers || []).map(p =>
      p.allocatedCostOverride ? (parseFloat(p.allocatedCost) || 0) : autoPerPax
    );

    const allocatedTotal = primaryDisplayed + addPaxDisplayed.reduce((s, v) => s + v, 0);
    const isBalanced = numPax <= 1 || Math.abs(allocatedTotal - totalCost) < 0.005;

    return { autoPerPax, primaryDisplayed, addPaxDisplayed, allocatedTotal, isBalanced };
  }, [totalCost, form.primaryAllocatedCost, form.primaryAllocatedCostOverride, form.additionalPassengers]);

  const addAdditionalPassenger = () =>
    setForm(p => ({
      ...p,
      additionalPassengers: [
        ...p.additionalPassengers,
        { _key: Math.random().toString(36).slice(2), name: '', passengerId: '', costCentre: '', role: 'traveller', sectorKeys: [], allocatedCost: '', allocatedCostOverride: false },
      ],
    }));

  const updateAdditionalPassenger = (i, updated) =>
    setForm(p => {
      const list = [...p.additionalPassengers];
      list[i] = updated;
      return { ...p, additionalPassengers: list };
    });

  const removeAdditionalPassenger = (i) =>
    setForm(p => ({ ...p, additionalPassengers: p.additionalPassengers.filter((_, j) => j !== i) }));

  // ── Policy variance checking ───────────────────────────────────────────────
  function findPolicyRate(destination, rates) {
    if (!rates || !destination) return null;
    if (rates[destination] !== undefined) return rates[destination];
    const lower = destination.toLowerCase();
    for (const [city, rate] of Object.entries(rates)) {
      if (city === 'All Cities') continue;
      if (city.toLowerCase() === lower || lower.includes(city.toLowerCase()) || city.toLowerCase().includes(lower)) return rate;
    }
    return rates['All Cities'] !== undefined ? rates['All Cities'] : null;
  }

  const varianceBreaches = useMemo(() => {
    const breaches = [];
    const accomV  = clientConfig?.policyVariance?.accommodation;
    const flightV = clientConfig?.policyVariance?.flight;

    if (accomV?.enabled) {
      form.sectors.filter(s => s.type === 'accommodation' && parseFloat(s.cost) > 0).forEach(s => {
        const nights = s.checkIn && s.checkOut
          ? Math.max(1, Math.round((new Date(s.checkOut) - new Date(s.checkIn)) / 86400000))
          : 1;
        const perNight = parseFloat(s.cost) / nights;
        const city = sectorReportingCity(s) || form.destinationCity || '';
        const policyRate = findPolicyRate(city, travelPolicy.rates);
        if (!policyRate) return;
        const threshold = accomV.type === 'percent'
          ? policyRate * (1 + (accomV.value || 0) / 100)
          : policyRate + (accomV.value || 0);
        if (perNight > threshold) {
          breaches.push({
            sectorType: 'accommodation',
            label: s.propertyName || 'Accommodation',
            city,
            cost: perNight,
            policyRate,
            threshold,
            excess: perNight - policyRate,
            excessPct: ((perNight - policyRate) / policyRate) * 100,
            action: accomV.action || 'warn',
            unit: 'per night',
          });
        }
      });
    }

    if (flightV?.enabled) {
      const flightSectors = form.sectors.filter(s => s.type === 'flight' && parseFloat(s.cost) > 0);
      if (flightSectors.length > 0) {
        const totalCost = flightSectors.reduce((sum, s) => sum + (parseFloat(s.cost) || 0), 0);
        const destination = form.destinationCity || '';
        const policyRate = findPolicyRate(destination, travelPolicy.flightRates);
        if (policyRate) {
          const threshold = flightV.type === 'percent'
            ? policyRate * (1 + (flightV.value || 0) / 100)
            : policyRate + (flightV.value || 0);
          if (totalCost > threshold) {
            breaches.push({
              sectorType: 'flight',
              label: 'Flights',
              city: destination,
              cost: totalCost,
              policyRate,
              threshold,
              excess: totalCost - policyRate,
              excessPct: ((totalCost - policyRate) / policyRate) * 100,
              action: flightV.action || 'warn',
              unit: 'total',
            });
          }
        }
      }
    }

    return breaches;
  }, [form.sectors, form.destinationCity, clientConfig?.policyVariance, travelPolicy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive trip start/end dates from the earliest and latest dates across all sectors
  const derivedDates = useMemo(() => {
    const dates = [];
    for (const s of form.sectors) {
      if (s.date)        dates.push(s.date);
      if (s.checkIn)     dates.push(s.checkIn);
      if (s.checkOut)    dates.push(s.checkOut);
      if (s.pickupDate)  dates.push(s.pickupDate);
      if (s.dropOffDate) dates.push(s.dropOffDate);
      if (s.entryDate)   dates.push(s.entryDate);
      if (s.exitDate)    dates.push(s.exitDate);
    }
    const sorted = dates.filter(Boolean).sort();
    return { start: sorted[0] || '', end: sorted[sorted.length - 1] || '' };
  }, [form.sectors]);

  useEffect(() => {
    if (derivedDates.start) set('startDate', derivedDates.start);
    if (derivedDates.end)   set('endDate',   derivedDates.end);
  }, [derivedDates.start, derivedDates.end]); // eslint-disable-line react-hooks/exhaustive-deps

  // Is this traveller exempt from approval altogether? Set per user by client_ops or STX — a CEO,
  // a board member, anyone whose travel nobody signs off. It is a decision about the person, not
  // something inferred from whether they have a manager.
  //
  // Keyed to the TRAVELLER, not whoever is filling the form in: an exempt person arranging travel
  // for someone else still routes that trip through the other person's approval.
  const travellerUser   = useMemo(
    () => teamMembers.find(m => m.id === form.travellerId) || null,
    [teamMembers, form.travellerId],
  );
  const travellerExempt = isApprovalExempt(travellerUser);

  // Approval required for this trip type — unless this traveller is exempt.
  const tripNeedsApproval = tripRequiresApproval(clientConfig, form.tripType) && !travellerExempt;

  // Approval routing follows the reporting line, so a traveller nobody is specifically accountable
  // for — a board member, a guest speaker, anyone with no approver named for them or managing them
  // — has no one the request properly belongs to. In that case the trip names its own approver
  // before it can be submitted. Only applies where the client actually routes approval by
  // hierarchy; see needsApproverNomination.
  const needsNominatedApprover = useMemo(
    () => tripNeedsApproval && needsApproverNomination(teamMembers, form.travellerId),
    [tripNeedsApproval, teamMembers, form.travellerId],
  );

  // Anyone in the client who can approve at all. Excludes STX (they override approval anyway, and
  // aren't the client's to nominate) and the traveller themselves.
  const nominatableApprovers = useMemo(
    () => eligibleApprovers(teamMembers)
      .filter(m => m.id !== form.travellerId)
      .sort((a, b) => memberName(a).localeCompare(memberName(b))),
    [teamMembers, form.travellerId],
  );

  const handleSave = async (submitForApproval = false) => {
    setError('');
    if (isSTX && !form.clientId) return setError('Please select a client for this trip.');
    if (!form.title.trim())         return setError('Trip title is required.');
    if (!form.travellerName.trim()) return setError('Traveller name is required.');
    // Required because the trip type decides whether the trip needs approval
    // (workflow.approvalByTripType), which fee applies, and how it reports. A trip saved without
    // one silently falls through the per-type overrides to the client's global requiresApproval
    // flag — which is how a client with approval switched off for every one of its trip types
    // still found trips sitting in Pending Approval.
    if (!form.tripType.trim()) return setError('Trip type is required — it decides whether this trip needs approval.');
    if (submitForApproval && needsNominatedApprover && !form.nominatedApproverId) {
      return setError('This traveller has no manager or named approver. Nominate who should approve this trip.');
    }
    if (canEditCostCentre && originalCostCentre !== '' && form.costCentre !== originalCostCentre && !form.costCentreChangeReason.trim()) {
      return setError('Please provide a reason for changing the cost centre.');
    }

    if (isSTX && !creditCheck.ok) return setError(creditCheck.error);

    const namedAddPax = form.additionalPassengers.filter(p => p.name?.trim());
    if (namedAddPax.length > 0 && !allocation.isBalanced) {
      return setError(
        `Passenger cost allocations (A$${allocation.allocatedTotal.toFixed(2)}) must equal the trip total ` +
        `(A$${totalCost.toFixed(2)}). Adjust the amounts or click "Reset to auto" to re-split evenly.`
      );
    }

    setSaving(true);
    try {
      const typedSectors = form.sectors.filter(s => s.type);
      const keyToIdx = {};
      typedSectors.forEach((s, i) => { keyToIdx[s._key] = i; });

      const sectors = typedSectors.map(({ _key, ...rest }) => rest);

      const additionalPassengers = form.additionalPassengers
        .map(({ _key, sectorKeys, allocatedCostOverride, ...rest }, origIdx) => ({
          ...rest,
          sectorIndices: (sectorKeys || []).map(k => keyToIdx[k]).filter(i => i !== undefined),
          allocatedCost: Math.round((allocation.addPaxDisplayed[origIdx] ?? 0) * 100) / 100,
        }))
        .filter(p => p.name?.trim());

      const primaryAllocatedCost = namedAddPax.length > 0
        ? Math.round(allocation.primaryDisplayed * 100) / 100
        : null;

      let status = trip?.status || 'draft';
      if (nuiteeBooked || flightBooked) {
        status = 'booked';
      } else if (submitForApproval) {
        status = tripRequiresApproval(clientConfig, form.tripType) ? 'pending_approval' : 'approved';
      } else if (status === 'declined') {
        status = 'draft';
      }

      // Force pending_approval if any variance breach requires explicit approval — unless this
      // traveller is exempt. The exemption says nobody signs their travel off, so a breach cannot
      // reintroduce an approver who does not exist. The breach is still recorded on the trip and
      // still shows in the policy reports; it just does not gate.
      const hasApproveBreaches = varianceBreaches.some(b => b.action === 'approve');
      if (hasApproveBreaches && !travellerExempt && status !== 'booked') status = 'pending_approval';

      // Drop a nomination that's no longer needed — e.g. the traveller was swapped for someone
      // inside the reporting line — so approval never routes to a stale name.
      const nomination = needsNominatedApprover
        ? { nominatedApproverId: form.nominatedApproverId, nominatedApproverName: form.nominatedApproverName }
        : { nominatedApproverId: '', nominatedApproverName: '' };

      await onSave({
        ...form,
        ...nomination,
        // Only STX can move credit, so a client save must not be able to clear an applied one by
        // omission. undefined means "leave the trip's existing credit alone".
        creditApplications: isSTX ? creditApplications : undefined,
        sectors,
        additionalPassengers,
        primaryAllocatedCost,
        status,
        totalCost,
        policyVarianceBreached: varianceBreaches.length > 0,
        varianceBreaches:       varianceBreaches.length > 0 ? varianceBreaches : [],
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isDraftOrDeclined = !trip || ['draft', 'declined'].includes(trip.status);

  // 'book' when the trip is approved or needs no approval; otherwise 'quote' — the hotel search
  // still runs, but it saves the chosen room to the sector for the approver instead of booking it.
  // This applies to STX as well as client staff: booking straight past a pending approval would
  // strand the request in the approver's queue and skip a policy-variance hold.
  const hotelMode = hotelFlowMode(trip?.status, tripNeedsApproval);
  // Flights follow the same rule — an unapproved trip saves a fare for the approver rather than
  // ticketing it. Ticketing is far less reversible than a hotel booking, so the gate matters more.
  const flightMode = flightFlowMode(trip?.status, tripNeedsApproval);

  // Cost centre editable by STX or client approvers/ops only — never by regular travellers
  const canEditCostCentre = isSTX || ['client_approver', 'client_ops'].includes(userProfile?.role);

  // Hotel booking feature gate — respects per-trip-type self-managed override
  const selfManagedHotelEnabled = clientConfig?.hotelBooking?.selfManagedHotelBooking !== false;
  const hotelBookingAllowedForTripType =
    clientConfig?.features?.hotelBooking !== false &&
    (form.tripType !== 'Self-Managed' || selfManagedHotelEnabled);

  // Flight booking gate. Issuing a ticket is irreversible in a way a hotel booking is not, so
  // client staff get it only where STX has switched `features.flightBooking` on for that client.
  // Strictly `=== true` — absent and false both mean off, so no client gains access by default.
  // STX are never gated. Client staff without it still see the button, disabled, so the capability
  // stays discoverable and they know to ask.
  const flightBookingEnabledForClient = clientConfig?.features?.flightBooking === true;
  const flightBookingLocked = !isSTX && !flightBookingEnabledForClient;

  return (
    <div className="space-y-5">
      {/* Nuitee hotel option saved banner */}
      {nuiteeOptionSaved !== null && !nuiteeBooked && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2 text-sm text-amber-900">
          <Hotel size={15} aria-hidden="true" className="text-amber-700 flex-shrink-0 mt-0.5" />
          <span>
            <strong>Hotel option saved{nuiteeOptionSaved ? ` — ${nuiteeOptionSaved}` : ''}.</strong>{' '}
            It is not booked. Save the trip and submit it for approval — the approver will see the hotel,
            dates and price. You can book it once the trip is approved.
          </span>
        </div>
      )}

      {/* Flight option saved banner */}
      {flightOptionSaved !== null && !flightBooked && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2 text-sm text-amber-900">
          <Plane size={15} aria-hidden="true" className="text-amber-700 flex-shrink-0 mt-0.5" />
          <span>
            <strong>Flight option saved{flightOptionSaved ? ` — ${flightOptionSaved}` : ''}.</strong>{' '}
            It is not ticketed. Save the trip and submit it for approval — the approver will see the
            flight, times and fare. Airline prices move, so it is re-priced live before ticketing.
          </span>
        </div>
      )}

      {/* Flight ticketed banner */}
      {flightBooked && (
        <div className="p-3 bg-sky-50 border border-sky-300 rounded-lg flex items-center gap-2 text-sm text-sky-900">
          <CheckCircle size={15} aria-hidden="true" className="text-sky-600 flex-shrink-0" />
          <span><strong>Flight booked via Nuitee.</strong> Save the trip below — status will update to <strong>Booked</strong> automatically.</span>
        </div>
      )}

      {/* Nuitee booking confirmed banner */}
      {nuiteeBooked && (
        <div className="p-3 bg-teal-50 border border-teal-300 rounded-lg flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-teal-800">
            <CheckCircle size={15} aria-hidden="true" className="text-teal-600 flex-shrink-0" />
            <span><strong>Hotel booked via Nuitee.</strong> Save the trip below — status will update to <strong>Booked</strong> automatically.</span>
          </div>
        </div>
      )}

      {/* Client selector — STX users only */}
      {isSTX && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <label className={lbl}>Client *</label>
          <select
            className={inp}
            aria-label="Client"
            value={form.clientId}
            onChange={e => set('clientId', e.target.value)}
          >
            <option value="">Select client…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {/* Common fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className={lbl}>Trip title *</label>
          <input
            className={inp}
            value={form.title}
            onChange={e => set('title', e.target.value)}
            placeholder="e.g. Jane Smith — NDIS Conference Melbourne"
          />
        </div>

        <div>
          <label className={lbl}>Traveller name *</label>
          <input
            className={inp}
            value={form.travellerName}
            onChange={e => {
              const name  = e.target.value;
              const lower = normName(name);
              // Match against passenger profiles first (preferred), then team members
              const paxMatch = passengers.find(p => passengerMatchesName(p, lower));
              if (paxMatch) {
                const linkedUser = paxMatch.userId ? teamMembers.find(m => m.id === paxMatch.userId) : null;
                const autoCC = linkedUser?.costCentre || '';
                if (autoCC) setOriginalCostCentre(autoCC);
                setForm(p => ({
                  ...p,
                  travellerName: name,
                  travellerId:   paxMatch.userId || '',
                  ...(autoCC ? { costCentre: autoCC } : {}),
                }));
              } else {
                const memberMatch = teamMembers.find(m =>
                  normName([m.firstName, m.lastName].filter(Boolean).join(' ')) === lower
                );
                const autoCC = memberMatch?.costCentre || '';
                if (autoCC) setOriginalCostCentre(autoCC);
                setForm(p => ({
                  ...p,
                  travellerName: name,
                  travellerId:   memberMatch ? memberMatch.id : '',
                  ...(autoCC ? { costCentre: autoCC } : {}),
                }));
              }
            }}
            placeholder="Type name or select from passenger profiles"
            list="trip-form-travellers"
            autoComplete="off"
          />
          <datalist id="trip-form-travellers">
            {passengers.map(p => (
              <option
                key={`pax-${p.id}`}
                value={[p.preferredName || p.firstName, p.lastName].filter(Boolean).join(' ')}
              />
            ))}
            {teamMembers
              .filter(m => !passengers.some(p => p.userId === m.id))
              .map(m => (
                <option key={`mem-${m.id}`} value={[m.firstName, m.lastName].filter(Boolean).join(' ')} />
              ))}
          </datalist>
          {matchedPassenger?.preferredName && matchedPassenger.preferredName !== matchedPassenger.firstName && (
            <div className="mt-1.5 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle size={13} aria-hidden="true" className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                <span className="font-semibold">Name mismatch:</span> Known as{' '}
                <span className="font-semibold">{matchedPassenger.preferredName}</span> but legal name is{' '}
                <span className="font-semibold">{matchedPassenger.firstName} {matchedPassenger.lastName}</span>.
                {' '}Ensure flight tickets are booked using the legal name.
              </p>
            </div>
          )}
        </div>

        {matchedPassenger && (
          <div className="col-span-2">
            <TravellerAccessibilityCard passenger={matchedPassenger} collapsible defaultExpanded={false} />
          </div>
        )}

        {travellerExempt && (
          <div className="col-span-2 p-3 bg-blue-50 border border-blue-300 rounded-lg flex items-start gap-2">
            <CheckCircle size={15} aria-hidden="true" className="text-blue-700 mt-0.5 shrink-0" />
            <p className="text-sm text-blue-900">
              <strong>{form.travellerName.trim() || 'This traveller'} does not require travel approval.</strong>{' '}
              Saving goes straight to approved — no approver is notified. Change this on their
              profile in Team if it is wrong.
            </p>
          </div>
        )}

        {/* No approver covers this traveller — the trip has to name one before it can be submitted */}
        {needsNominatedApprover && (
          <div className="col-span-2 p-3 bg-amber-50 border border-amber-300 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle size={15} aria-hidden="true" className="text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-amber-900">
                  <strong>An approver must be nominated for this trip.</strong>{' '}
                  {form.travellerName.trim() || 'This traveller'} has no manager or named approver in
                  this organisation's approval hierarchy, so nobody is accountable for signing this
                  off. Choose who should approve it.
                </p>
                <label className={`${lbl} mt-3`} htmlFor="trip-nominated-approver">
                  Nominated approver *
                </label>
                {nominatableApprovers.length > 0 ? (
                  <select
                    id="trip-nominated-approver"
                    className={inp}
                    value={form.nominatedApproverId}
                    onChange={e => {
                      const m = nominatableApprovers.find(x => x.id === e.target.value);
                      setForm(p => ({
                        ...p,
                        nominatedApproverId:   m ? m.id : '',
                        nominatedApproverName: m ? memberName(m) : '',
                      }));
                    }}
                  >
                    <option value="">Select an approver…</option>
                    {nominatableApprovers.map(m => (
                      <option key={m.id} value={m.id}>
                        {memberName(m)}{ROLE_LABELS[m.role] ? ` — ${ROLE_LABELS[m.role]}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-amber-900" role="alert">
                    No one in this organisation currently has approval permission. Ask STX to grant
                    someone the “Approve trips” permission before submitting this trip.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div>
          <label className={lbl}>Trip type *</label>
          <select className={inp} aria-label="Trip type" value={form.tripType} onChange={e => set('tripType', e.target.value)}>
            <option value="">Select…</option>
            {tripTypes.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>

        <div>
          <label className={lbl}>
            Start date <span className="text-gray-500 font-normal">(auto from sectors)</span>
          </label>
          <input
            type="date"
            className={`${inp} bg-gray-50 text-gray-500 cursor-not-allowed`}
            value={form.startDate}
            readOnly
            aria-readonly="true"
            aria-label="Start date — automatically set from the earliest sector date"
          />
        </div>

        <div>
          <label className={lbl}>
            End date <span className="text-gray-500 font-normal">(auto from sectors)</span>
          </label>
          <input
            type="date"
            className={`${inp} bg-gray-50 text-gray-500 cursor-not-allowed`}
            value={form.endDate}
            readOnly
            aria-readonly="true"
            aria-label="End date — automatically set from the latest sector date"
          />
        </div>

        <div>
          <label className={lbl}>Origin city</label>
          <input
            className={inp}
            list="trip-form-cities"
            value={form.originCity}
            onChange={e => set('originCity', e.target.value)}
            placeholder="e.g. Brisbane"
            autoComplete="off"
          />
        </div>

        <div>
          <label className={lbl}>Destination city</label>
          <input
            className={inp}
            list="trip-form-cities"
            value={form.destinationCity}
            onChange={e => set('destinationCity', e.target.value)}
            placeholder="e.g. Sydney"
            autoComplete="off"
          />
        </div>

        <datalist id="trip-form-cities">
          {CITIES.map(c => <option key={c} value={c} />)}
        </datalist>

        {/* Cost centre — editable by STX/approvers only on existing trips */}
        {costCentres.length > 0 && canEditCostCentre && (
          <div>
            <label className={lbl}>Cost centre</label>
            <select className={inp} aria-label="Cost centre" value={form.costCentre} onChange={e => set('costCentre', e.target.value)}>
              <option value="">Select…</option>
              {costCentres.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        )}
        {costCentres.length > 0 && !canEditCostCentre && form.costCentre && (
          <div>
            <label className={lbl}>Cost centre</label>
            <p className={`${inp} bg-gray-50 text-gray-600 cursor-default`}>{form.costCentre}</p>
          </div>
        )}

        {/* Reason required when STX/approver changes cost centre away from the traveller's default */}
        {canEditCostCentre && originalCostCentre !== '' && form.costCentre !== originalCostCentre && (
          <div className="col-span-2">
            <label className={`${lbl} text-amber-600`}>Reason for cost centre change *</label>
            <textarea
              className={`${inp} border-amber-300 focus:ring-amber-500`}
              rows={2}
              value={form.costCentreChangeReason}
              onChange={e => set('costCentreChangeReason', e.target.value)}
              placeholder="Explain why the cost centre is being changed for this trip…"
            />
          </div>
        )}

        <div className="col-span-2">
          <label className={lbl}>Purpose / notes</label>
          <textarea
            className={inp}
            rows={2}
            value={form.purpose}
            onChange={e => set('purpose', e.target.value)}
            placeholder="Reason for travel, event details, accessibility context…"
          />
        </div>

        {isSTX && (
          <div className="col-span-2">
            <label className={lbl}>STX internal notes (not visible to client)</label>
            <textarea
              className={inp}
              rows={2}
              value={form.internalNotes}
              onChange={e => set('internalNotes', e.target.value)}
            />
          </div>
        )}

        {isSTX && (
          <div>
            <label className={lbl}>VTO Trip ID (STX internal — not visible to client)</label>
            <input
              className={inp}
              value={form.vtoTripId}
              onChange={e => set('vtoTripId', e.target.value)}
              placeholder="e.g. VTO-12345"
            />
          </div>
        )}

        {isSTX && form.tripType && form.tripType !== 'Self-Managed' && (
          <div>
            <label className={lbl}>Digital Itinerary link</label>
            <input
              type="url"
              className={inp}
              value={form.digitalItineraryLink}
              onChange={e => set('digitalItineraryLink', e.target.value)}
              placeholder="https://…"
            />
          </div>
        )}
      </div>

      {/* Sectors */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Sectors</h3>
          {totalCost > 0 && (
            <span className="text-sm text-gray-700">
              Est. total: <strong className="text-gray-800">A${totalCost.toFixed(2)}</strong>
            </span>
          )}
        </div>

        <div className="space-y-2">
          {form.sectors.map((s, i) => (
            <SectorCard
              key={s._key}
              sector={s}
              index={i}
              onChange={updated => updateSector(i, updated)}
              onRemove={() => removeSector(i)}
              tripDestinationCity={form.destinationCity}
              onOpenHotelBooking={
                s.type === 'accommodation' && (isSTX || hotelBookingAllowedForTripType)
                  ? () => openHotelBooking(i)
                  : undefined
              }
              hotelMode={hotelMode}
              onOpenFlightBooking={
                // Not on a return leg — it is part of the outbound's booking, not its own.
                s.type === 'flight' && !s.returnLegOf ? () => openFlightBooking(i) : undefined
              }
              flightMode={flightMode}
              flightBookingLocked={flightBookingLocked}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={addSector}
          className="mt-3 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 py-1"
        >
          <Plus size={14} aria-hidden="true" /> Add sector
        </button>

        {/* Policy variance warnings */}
        {varianceBreaches.length > 0 && (
          <div className={`mt-3 rounded-xl border p-4 space-y-2 ${
            varianceBreaches.some(b => b.action === 'approve')
              ? 'bg-red-50 border-red-200'
              : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex items-center gap-2">
              <AlertTriangle size={15} aria-hidden="true" className={varianceBreaches.some(b => b.action === 'approve') ? 'text-red-600' : 'text-amber-600'} />
              <p className={`text-sm font-semibold ${varianceBreaches.some(b => b.action === 'approve') ? 'text-red-800' : 'text-amber-800'}`}>
                {varianceBreaches.some(b => b.action === 'approve')
                  ? 'Policy variance exceeded — approval required before this trip can proceed'
                  : 'Policy variance exceeded — please review before submitting'}
              </p>
            </div>
            <div className="space-y-1">
              {varianceBreaches.map((b, i) => (
                <div key={i} className="text-xs text-gray-700 pl-5 flex items-start gap-1">
                  <span>
                    <span className="font-medium">{b.label}</span>
                    {b.city && ` (${b.city})`}:
                    {' '}A${b.cost.toFixed(2)} {b.unit} vs policy A${b.policyRate.toFixed(2)}
                    {' '}— <span className="font-medium">{b.excessPct > 0 ? '+' : ''}{b.excessPct.toFixed(1)}% over policy</span>
                    {' '}(threshold A${b.threshold.toFixed(2)})
                  </span>
                  {b.action === 'approve'
                    ? <span className="ml-1 text-red-600 font-medium shrink-0">→ Requires approval</span>
                    : <span className="ml-1 text-amber-600 shrink-0">→ Warning only</span>
                  }
                </div>
              ))}
            </div>
            {varianceBreaches.some(b => b.action === 'approve') && (
              <p className="text-xs text-red-600 pl-5">
                This trip will be placed into approval review when saved.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Travel credit — STX only. Credit the client has already paid on an earlier trip for this
          passenger, applied here to reduce what this trip bills. Sectors stay at full price. */}
      {isSTX && creditPassengerId && availableCredits.length > 0 && (
        <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-lg">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                <Wallet size={15} aria-hidden="true" className="text-emerald-700" />
                Travel credit available
              </h3>
              <p className="text-xs text-emerald-800 mt-1">
                {matchedPassenger?.firstName || 'This passenger'} holds{' '}
                <strong>A${creditBalance(availableCredits).toFixed(2)}</strong> in credit from earlier
                travel. Applying it reduces what this trip bills — the sectors above stay at full price.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                const target = Math.min(creditBalance(availableCredits), tripGross);
                const alloc  = allocateOldestFirst(target, availableCredits);
                const next   = {};
                for (const a of alloc) next[a.creditId] = String(a.amountInclGST);
                setCreditDraft(next);
              }}
              className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 whitespace-nowrap"
            >
              Use max
            </button>
          </div>

          <div className="space-y-2">
            {availableCredits.map(c => (
              <div key={c.id} className="flex items-center gap-3 bg-white border border-emerald-200 rounded-lg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 truncate">{c.reason || 'Travel credit'}</p>
                  <p className="text-xs text-gray-600">
                    A${(parseFloat(c.remainingInclGST) || 0).toFixed(2)} remaining
                    {(c.sourceTripRefs || []).length > 0 && ` · from ${c.sourceTripRefs.join(', ')}`}
                    {(c.gstRate ?? 0.1) === 0 && ' · GST-free'}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-700">A$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    max={c.remainingInclGST}
                    className="w-28 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-emerald-500 placeholder:text-gray-500"
                    placeholder="0.00"
                    aria-label={`Credit to apply from ${c.reason || 'travel credit'}`}
                    value={creditDraft[c.id] ?? ''}
                    onChange={e => setCreditDraft(prev => ({ ...prev, [c.id]: e.target.value }))}
                  />
                </div>
              </div>
            ))}
          </div>

          {!creditCheck.ok && (
            <p role="alert" className="mt-2 text-sm text-red-700">{creditCheck.error}</p>
          )}

          {creditTotal > 0 && creditCheck.ok && (
            <div className="mt-3 pt-3 border-t border-emerald-200 text-sm space-y-1">
              <div className="flex justify-between text-gray-700">
                <span>Trip total (incl. GST)</span>
                <span className="tabular-nums">A${tripGross.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-emerald-800">
                <span>Travel credit applied</span>
                <span className="tabular-nums">&minus; A${creditTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold text-gray-900 pt-1 border-t border-emerald-200">
                <span>Client will be billed</span>
                <span className="tabular-nums">A${netTripTotal.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Additional Passengers */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Additional Passengers</h3>
            {form.additionalPassengers.length === 0 && (
              <p className="text-xs text-gray-600 mt-0.5">Add passengers travelling on this trip (each can be assigned to specific sectors and cost centres)</p>
            )}
          </div>
          <button
            type="button"
            onClick={addAdditionalPassenger}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus size={14} aria-hidden="true" /> Add passenger
          </button>
        </div>

        {form.additionalPassengers.length > 0 && (
          <div className="space-y-3">
            {/* Primary traveller cost row */}
            <div className="border border-blue-200 rounded-lg p-3 bg-blue-50 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Passenger 1 — Primary</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-gray-800 flex-1">{form.travellerName || '—'}</span>
                {form.costCentre && <span className="text-xs text-gray-600">{form.costCentre}</span>}
              </div>
              <div>
                <label className={lbl}>Allocated cost incl. GST (A$)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min="0" step="0.01"
                    className={`${inp} w-36`}
                    value={form.primaryAllocatedCostOverride
                      ? (form.primaryAllocatedCost ?? '')
                      : allocation.autoPerPax.toFixed(2)}
                    onChange={e => setForm(p => ({ ...p, primaryAllocatedCost: e.target.value, primaryAllocatedCostOverride: true }))}
                  />
                  {form.primaryAllocatedCostOverride ? (
                    <button
                      type="button"
                      onClick={() => setForm(p => ({ ...p, primaryAllocatedCostOverride: false, primaryAllocatedCost: '' }))}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Reset to auto
                    </button>
                  ) : (
                    <span className="text-xs text-gray-600">
                      Auto-split ({1 + form.additionalPassengers.length} passengers)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Additional passenger cards */}
            {form.additionalPassengers.map((pax, i) => (
              <AdditionalPassengerCard
                key={pax._key}
                pax={pax}
                index={i}
                sectors={form.sectors}
                passengers={passengers}
                teamMembers={teamMembers}
                costCentres={costCentres}
                autoAllocated={allocation.autoPerPax}
                onChange={updated => updateAdditionalPassenger(i, updated)}
                onRemove={() => removeAdditionalPassenger(i)}
              />
            ))}

            {/* Balance indicator */}
            <div className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm border ${
              allocation.isBalanced
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <span className={`font-medium ${allocation.isBalanced ? 'text-green-700' : 'text-red-700'}`}>
                {allocation.isBalanced ? '✓ Allocations balanced' : '⚠ Allocations unbalanced — cannot save'}
              </span>
              <span className={`text-xs tabular-nums ${allocation.isBalanced ? 'text-green-600' : 'text-red-600'}`}>
                A${allocation.allocatedTotal.toFixed(2)} allocated / A${totalCost.toFixed(2)} total
                {!allocation.isBalanced && (
                  <span className="ml-1 font-semibold">
                    ({allocation.allocatedTotal > totalCost ? '+' : ''}{(allocation.allocatedTotal - totalCost).toFixed(2)})
                  </span>
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Travel Insurance */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Travel Insurance</h3>
          <button
            type="button"
            onClick={() => {
              if (form.travelInsurance) {
                set('travelInsurance', null);
              } else {
                set('travelInsurance', { provider: '', policyNumber: '', coverFrom: '', coverTo: '', emergencyPhone: '', notes: '' });
              }
            }}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              form.travelInsurance
                ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700'
                : 'border-gray-200 text-gray-700 hover:border-gray-300'
            }`}
          >
            {form.travelInsurance ? 'Remove insurance' : '+ Add insurance'}
          </button>
        </div>
        {form.travelInsurance && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Insurance provider">
              <input
                className={inp}
                value={form.travelInsurance.provider || ''}
                onChange={e => set('travelInsurance', { ...form.travelInsurance, provider: e.target.value })}
                placeholder="e.g. Cover-More, Allianz"
              />
            </F>
            <F label="Policy number">
              <input
                className={inp}
                value={form.travelInsurance.policyNumber || ''}
                onChange={e => set('travelInsurance', { ...form.travelInsurance, policyNumber: e.target.value })}
                placeholder="e.g. POL-123456"
              />
            </F>
            <F label="Cover from">
              <input
                type="date"
                className={inp}
                value={form.travelInsurance.coverFrom || ''}
                onChange={e => set('travelInsurance', { ...form.travelInsurance, coverFrom: e.target.value })}
              />
            </F>
            <F label="Cover to">
              <input
                type="date"
                className={inp}
                value={form.travelInsurance.coverTo || ''}
                onChange={e => set('travelInsurance', { ...form.travelInsurance, coverTo: e.target.value })}
              />
            </F>
            <F label="24h emergency phone" span2>
              <input
                className={inp}
                value={form.travelInsurance.emergencyPhone || ''}
                onChange={e => set('travelInsurance', { ...form.travelInsurance, emergencyPhone: e.target.value })}
                placeholder="+61 1300 xxx xxx"
              />
            </F>
            <F label="Notes" span2>
              <textarea
                className={inp}
                rows={2}
                value={form.travelInsurance.notes || ''}
                onChange={e => set('travelInsurance', { ...form.travelInsurance, notes: e.target.value })}
                placeholder="Policy exclusions, pre-existing conditions, coverage details…"
              />
            </F>
          </div>
        )}
      </div>

      {error && (
        <p id="tripform-error" role="alert" aria-live="assertive" className="text-red-700 text-sm">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        {isDraftOrDeclined ? (
          <>
            <button
              type="button"
              onClick={() => handleSave(false)}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save draft'}
            </button>
            <button
              type="button"
              onClick={() => handleSave(true)}
              disabled={saving}
              className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving
                ? (tripNeedsApproval ? 'Submitting…' : 'Saving…')
                : (tripNeedsApproval ? 'Submit for approval' : 'Save & approve')}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        )}
      </div>
    </div>
  );
}
